import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './components/ui/table';
import { generateClient } from 'aws-amplify/api';
// import { listQuotes } from './graphql/queries'; // MORK TODO - this is fetching too many quotes
import { quotesByUserId } from './graphql/queries';
import { getCurrentUser } from 'aws-amplify/auth';
import { flatConfigFromQuoteRow } from './utils/quoteLoad';
import { migrateQuoteConfig } from './utils/migration';
import { formatDriveCapacity } from './utils/configUtilities';


const QuotesModal = ({ isOpen, onClose, onLoadConfig, userEmail, hasFullConfigurator }) => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen) {
      loadQuotes();
    }
  }, [isOpen]);

  const loadQuotes = async () => {
    try {
      setLoading(true);
      const client = generateClient();
      const { username } = await getCurrentUser();

      let allQuotes = [];
      let nextToken = null;

      do {
        const response = await client.graphql({
          query: quotesByUserId,
          variables: {
            userId: username,
            limit: 100,
            nextToken: nextToken
          }
        });

        allQuotes = [...allQuotes, ...response.data.quotesByUserId.items];
        nextToken = response.data.quotesByUserId.nextToken;
      } while (nextToken);

      const parsedQuotes = allQuotes.map(quote => ({
        ...quote,
        config: flatConfigFromQuoteRow(quote),
        metrics: JSON.parse(quote.metrics),
        createdAt: new Date(quote.createdAt)
      })).sort((a, b) => b.createdAt - a.createdAt);

      setQuotes(parsedQuotes);
    } catch (error) {
      console.error('Error loading quotes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleQuoteClick = (quote) => {
    onLoadConfig(quote.config, quote.config.systemType, quote);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" role="dialog">
      <div className="w-full max-w-7xl">
        <Card className="bg-gray-800 text-white">
          <CardHeader className="bg-gray-700">
            <div className="flex justify-between items-center">
              <CardTitle className="text-xl font-bold text-white">Saved Quotes</CardTitle>
              <button
                onClick={onClose}
                className="text-white hover:text-gray-300 p-2"
                aria-label="Close modal"
              >
                ✕
              </button>
            </div>
          </CardHeader>
          <CardContent className="max-h-[60vh] overflow-auto">
            {loading ? (
              <div className="text-center py-4">Loading quotes...</div>
            ) : quotes.length === 0 ? (
              <div className="text-center py-4">No saved quotes found</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-b border-gray-700">
                    <TableHead className="text-white">Date Created</TableHead>
                    <TableHead className="text-white">Description</TableHead>
                    <TableHead className="text-white">User</TableHead>
                    <TableHead className="text-white">System Type</TableHead>
                    <TableHead className="text-white">RAW Capacity</TableHead>
                    <TableHead className="text-white">Director Count</TableHead>
                    <TableHead className="text-white">Director SSD</TableHead>
                    <TableHead className="text-white">Hybrid Count</TableHead>
                    <TableHead className="text-white">Hybrid SSD</TableHead>
                    <TableHead className="text-white">HDD Size</TableHead>
                    <TableHead className="text-white">AFA Server Count</TableHead>
                    <TableHead className="text-white">AFA SSD</TableHead>
                    {hasFullConfigurator && (
                      <TableHead className="text-white text-right">Total Cost</TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {quotes.map((quote) => (
                    <TableRow 
                      key={quote.quoteId} 
                      className="border-b border-gray-700 hover:bg-gray-700/50 cursor-pointer"
                      onClick={() => handleQuoteClick(quote)}
                    >
                      <TableCell className="text-gray-300">
                        {quote.createdAt.toLocaleDateString()} {quote.createdAt.toLocaleTimeString()}
                      </TableCell>
                      <TableCell className="text-gray-300">
                        {quote.description}
                      </TableCell>
                      <TableCell className="text-gray-300">
                        {userEmail || quote.userId}
                      </TableCell>
                      <TableCell className="text-gray-300">
                        {quote.config.systemType === 'BaseSystem' ? 'Base' : quote.config.systemType || 'Base'}
                      </TableCell>
                      <TableCell className="text-right text-gray-300">
                        {quote.metrics.totalRawCapacity.toLocaleString(undefined, { maximumFractionDigits: 0 })} PB
                      </TableCell>
                      <TableCell className="text-center text-gray-300">
                        {quote.config.veloCount.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                      </TableCell>
                      <TableCell className="text-right text-gray-300">
                        {formatDriveCapacity(quote.config.veloSsdCapacity)} TB
                      </TableCell>
                      <TableCell className="text-center text-gray-300">
                        {quote.config.hybridNodeCount.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                      </TableCell>
                      <TableCell className="text-right text-gray-300">
                        {formatDriveCapacity(quote.config.hybridVpodSsdCapacity)} TB
                      </TableCell>
                      <TableCell className="text-right text-gray-300">
                        {formatDriveCapacity(quote.config.hybridHddCapacity)} TB
                      </TableCell>
                      <TableCell className="text-center text-gray-300">
                        {quote.config.afaNodeCount.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                      </TableCell>
                      <TableCell className="text-right text-gray-300">
                        {formatDriveCapacity(quote.config.afaVpodSsdCapacity)} TB
                      </TableCell>
                      {hasFullConfigurator && (
                        <TableCell className="text-right text-gray-300">
                          ${quote.metrics.totalSolutionCost.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default QuotesModal;